import { expressApi } from '@/state/api/expressApi';
import type {
  BatchResponseDto,
  BatchUploadInitRequest,
  BatchUploadInitResponse,
  PageDto,
  DocumentSummaryDto,
  UploadFormatsResponse,
  UploadLanguagesResponse,
} from '@/types';

export interface ListDocumentsArgs {
  uploadBatchId: string;
  size?: number;
}

export interface ListDocumentsResult {
  data: PageDto<DocumentSummaryDto>;
  retryAfter: number | null;
}

export const uploadApi = expressApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * GET /uploads/formats — the file extensions this deployment accepts, e.g.
     * `{ extensions: ['txt', 'pdf'] }`. It changes only when the API is
     * redeployed, so one fetch serves the session.
     */
    getUploadFormats: builder.query<UploadFormatsResponse, void>({
      query: () => '/uploads/formats',
      keepUnusedDataFor: 3600,
    }),

    /**
     * GET /uploads/languages — the target languages and their regional variants
     * (COPA-855), already narrowed to what this deployment can translate into.
     * The catalog changes only with an API release, so one fetch serves the session.
     */
    getUploadLanguages: builder.query<UploadLanguagesResponse, void>({
      query: () => '/uploads/languages',
      keepUnusedDataFor: 3600,
    }),

    /**
     * POST /uploads — phase 1 of the presigned upload: create the batch and
     * return one presigned multipart S3 POST target per file.
     * Body: the file manifest (filename/contentType/size) + upload metadata.
     * The client then uploads each file directly to S3 (see
     * services/storageUpload) and confirms with `completeUpload`.
     *
     * Returns 409 (application/problem+json, `active-workflow-exists`) when the
     * user already has a translation in progress — handled in UploadPage.
     */
    initUpload: builder.mutation<BatchUploadInitResponse, BatchUploadInitRequest>({
      query: (body) => ({
        url: '/uploads',
        method: 'POST',
        body,
      }),
    }),

    /**
     * POST /uploads/{uploadBatchId}/complete — phase 3: confirm every file has
     * been uploaded to S3 and enqueue the batch for processing. Sent without a
     * body. Returns 202 + BatchResponseDto with the batch's initial document
     * items (and the XTM Cloud project id, which is created at this point).
     */
    completeUpload: builder.mutation<BatchResponseDto, string>({
      query: (uploadBatchId) => ({
        url: `/uploads/${uploadBatchId}/complete`,
        method: 'POST',
      }),
      invalidatesTags: ['Documents'],
    }),

    /**
     * GET /documents?uploadBatchId={id}&size=100 — list documents for a batch.
     * Handles Retry-After header for polling: the caller should re-fetch
     * after the indicated number of seconds while documents are processing.
     */
    listDocuments: builder.query<ListDocumentsResult, ListDocumentsArgs>({
      query: ({ uploadBatchId, size = 100 }) => ({
        url: '/documents',
        params: { uploadBatchId, size },
      }),
      transformResponse: (response: PageDto<DocumentSummaryDto>, meta) => {
        const retryAfterHeader = meta?.response?.headers.get('Retry-After');
        const retryAfter = retryAfterHeader ? Number.parseInt(retryAfterHeader, 10) : null;
        return { data: response, retryAfter };
      },
      providesTags: ['Documents'],
    }),
  }),
});

export const {
  useGetUploadFormatsQuery,
  useGetUploadLanguagesQuery,
  useInitUploadMutation,
  useCompleteUploadMutation,
  useListDocumentsQuery,
  useLazyListDocumentsQuery,
} = uploadApi;
